import type { CanvasSettings, EditorElement } from '../../../editor/types';

export const DEFAULT_EDITOR_CANVAS_WIDTH = 900;
export const DEFAULT_EDITOR_CANVAS_HEIGHT = 600;
export const MAX_EDITOR_CANVAS_WIDTH = 2700;
export const MAX_EDITOR_CANVAS_HEIGHT = 2400;

export const cloneCanvasSettings = (settings: CanvasSettings): CanvasSettings => ({
  ...settings,
  padding: { ...settings.padding },
  innerPadding: settings.innerPadding ? { ...settings.innerPadding } : undefined,
  grid: { ...settings.grid },
  hiddenArea: { ...settings.hiddenArea },
  question: settings.question ? { ...settings.question } : {},
});

export const clampNumber = (value: string, fallback: number, min = 0, max?: number): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  const rounded = Math.round(parsed);
  const lowerBound = Math.max(min, rounded);
  if (typeof max === 'number') {
    return Math.min(max, lowerBound);
  }
  return lowerBound;
};

/**
 * Check if two rectangles overlap
 */
const doRectsOverlap = (
  rect1: { x: number; y: number; width: number; height: number },
  rect2: { x: number; y: number; width: number; height: number }
): boolean => {
  return !(
    rect1.x + rect1.width <= rect2.x ||
    rect2.x + rect2.width <= rect1.x ||
    rect1.y + rect1.height <= rect2.y ||
    rect2.y + rect2.height <= rect1.y
  );
};

/**
 * Find an optimal position for a new element that avoids overlapping existing elements
 */
export const findNonOverlappingPosition = (
  preferredX: number,
  preferredY: number,
  elementWidth: number,
  elementHeight: number,
  existingElements: EditorElement[],
  canvasWidth: number,
  canvasHeight: number,
  innerPadding: { top: number; right: number; bottom: number; left: number }
): { x: number; y: number } => {
  const SPACING = 20; // Minimum spacing between elements
  const GRID_STEP = 40; // Step size for grid search

  // Calculate available canvas area (in left/top coordinates)
  const minX = innerPadding.left;
  const minY = innerPadding.top;
  const maxX = canvasWidth - innerPadding.right - elementWidth;
  const maxY = canvasHeight - innerPadding.bottom - elementHeight;

  // Convert center coordinates to left/top coordinates
  // preferredX and preferredY are center points
  const preferredCenterX = preferredX;
  const preferredCenterY = preferredY;

  // Calculate left/top from center, then clamp to bounds
  let preferredLeft = preferredCenterX - elementWidth / 2;
  let preferredTop = preferredCenterY - elementHeight / 2;

  preferredLeft = Math.max(minX, Math.min(maxX, preferredLeft));
  preferredTop = Math.max(minY, Math.min(maxY, preferredTop));

  const hasOverlap = existingElements.some(el => {
    const elWidth = el.size.width || 200;
    const elHeight = el.size.height || 120;
    return doRectsOverlap(
      { x: preferredLeft, y: preferredTop, width: elementWidth + SPACING, height: elementHeight + SPACING },
      { x: el.position.x - elWidth / 2, y: el.position.y - elHeight / 2, width: elWidth, height: elHeight }
    );
  });

  // If no overlap at preferred position, use it
  if (!hasOverlap) {
    return { x: preferredLeft + elementWidth / 2, y: preferredTop + elementHeight / 2 };
  }

  // Search for nearest non-overlapping position using spiral pattern
  const searchRadius = Math.max(canvasWidth, canvasHeight);

  for (let radius = GRID_STEP; radius < searchRadius; radius += GRID_STEP) {
    // Try positions in a spiral around the preferred position
    const positions = [
      { x: preferredLeft, y: preferredTop - radius }, // Top
      { x: preferredLeft + radius, y: preferredTop }, // Right
      { x: preferredLeft, y: preferredTop + radius }, // Bottom
      { x: preferredLeft - radius, y: preferredTop }, // Left
      { x: preferredLeft + radius, y: preferredTop - radius }, // Top-right
      { x: preferredLeft + radius, y: preferredTop + radius }, // Bottom-right
      { x: preferredLeft - radius, y: preferredTop + radius }, // Bottom-left
      { x: preferredLeft - radius, y: preferredTop - radius }, // Top-left
    ];

    for (const pos of positions) {
      // Check if position is within canvas bounds
      if (pos.x < minX || pos.x > maxX || pos.y < minY || pos.y > maxY) {
        continue;
      }

      // Check if position overlaps with any existing element
      const overlaps = existingElements.some(el => {
        const elWidth = el.size.width || 200;
        const elHeight = el.size.height || 120;
        return doRectsOverlap(
          { x: pos.x, y: pos.y, width: elementWidth + SPACING, height: elementHeight + SPACING },
          { x: el.position.x - elWidth / 2, y: el.position.y - elHeight / 2, width: elWidth, height: elHeight }
        );
      });

      if (!overlaps) {
        return { x: pos.x + elementWidth / 2, y: pos.y + elementHeight / 2 };
      }
    }
  }

  // If no non-overlapping position found, place at bottom of canvas
  const bottomY = existingElements.reduce((maxY, el) => {
    const elHeight = el.size.height || 120;
    const elBottom = el.position.y + elHeight / 2;
    return Math.max(maxY, elBottom);
  }, minY);

  return {
    x: minX + elementWidth / 2 + SPACING,
    y: bottomY + elementHeight / 2 + SPACING
  };
};
